<template>
	<ModalV2
		:value="value"
		:labelled-by="`interview-title-${_uid}`"
		panel-class="flex max-h-[calc(100dvh-32px)] max-w-[800px] flex-col overflow-hidden rounded-xl border border-grey-200"
		body-class="min-h-0 flex-1 px-0 pb-0"
		:max-body-vh="0"
		footer-class="shrink-0 border-t border-grey-200 px-6 py-4 md:justify-between"
		:close-on-backdrop="!busy"
		:close-on-esc="!busy"
		@input="!$event && requestClose()"
	>
		<template #header>
			<header class="flex shrink-0 items-center gap-4 p-6">
				<div
					class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e6f4ec] text-[#2b503c]"
				>
					<IconCalendarPlus :size="20" />
				</div>
				<div class="min-w-0 flex-1">
					<h2
						:id="`interview-title-${_uid}`"
						class="m-0 font-sans text-base font-medium normal-case text-grey-1000"
					>
						{{ $t('application.interview-invitation.title') }}
					</h2>
					<p v-if="context" class="mb-0 mt-1 text-sm text-grey-700">
						{{
							$t('application.interview-invitation.subtitle', {
								candidate: context.sample.name
							})
						}}
					</p>
				</div>
				<Button
					type="tertiary"
					class="h-8 w-8 shrink-0 p-2"
					:disabled="busy"
					:aria-label="$t('application.decline.close')"
					data-test="interview-close"
					@click="requestClose"
					><IconTimes :size="16"
				/></Button>
			</header>
		</template>
		<div
			class="flex gap-3 border-t border-grey-200 bg-[#fef0c7] px-6 py-4 text-sm text-[#78300f]"
		>
			<IconAlertTriangle :size="20" class="shrink-0" />
			<p class="m-0">
				{{ $t('application.interview-invitation.warning') }}
			</p>
		</div>
		<div
			v-if="errorKey"
			role="alert"
			class="m-4 rounded-lg bg-grey-50 p-3 text-sm text-error"
		>
			{{ $t(errorKey) }}
			<Button v-if="!attemptPayload" type="link" @click="fetchPreview">{{
				$t('application.decline.refresh')
			}}</Button>
		</div>

		<div v-if="context && needsContacts" class="p-6">
			<AddressChips
				:key="'contacts-' + selectedId"
				ref="contacts"
				v-model="contactEmails"
				:id="`interview-contacts-${_uid}`"
				required
				:disabled="fieldsLocked"
				:label="$t('email-templates.contacts')"
				:help="$t('application.interview-invitation.contacts-help')"
				@pending="pendingContacts = $event"
			/>
			<p
				v-if="contactsRequired"
				role="alert"
				class="mb-0 mt-2 text-sm text-error"
			>
				{{ $t('application.interview-invitation.contacts-required') }}
			</p>
		</div>
		<div
			v-if="value"
			class="grid min-w-0 grid-cols-1 border-t border-grey-200 md:grid-cols-[240px_minmax(0,1fr)]"
		>
			<div
				class="min-w-0 border-b border-grey-200 p-3 md:border-b-0 md:border-r"
			>
				<TemplateSelector
					:key="opening"
					v-model="selectedId"
					:search-templates="searchTemplates"
					:disabled="fieldsLocked"
					hide-search-when-single
					selection-check
					:search-placeholder="
						$t('application.interview-invitation.search')
					"
					:label="
						$t('application.interview-invitation.choose-template')
					"
				/>
			</div>
			<div class="min-w-0">
				<h3
					v-if="context"
					class="m-0 border-b border-grey-200 p-4 font-sans text-sm font-medium normal-case text-grey-1000"
				>
					{{ context.template.name }}
				</h3>
				<TemplatePreview
					message-only
					:preview="context && context.preview"
					:loading="previewLoading"
					:error="!!errorKey && !attemptPayload"
					:error-message="
						$t('application.interview-invitation.preview-error')
					"
					:bcc="effectiveBcc"
				>
					<template v-if="context && !configuredBcc.length" #bcc>
						<details ref="bccEditor">
							<summary class="cursor-pointer">{{
								effectiveBcc.join(', ') || '–'
							}}</summary>
							<AddressChips
								:key="'bcc-' + selectedId"
								ref="bcc"
								v-model="manualBcc"
								:id="`interview-bcc-${_uid}`"
								class="mt-3"
								:limit="3"
								:disabled="fieldsLocked"
								:label="$t('email-templates.bcc')"
								:help="$t('application.decline.bcc-help')"
							/>
						</details>
					</template>
				</TemplatePreview>
			</div>
		</div>
		<template #footer>
			<Button
				v-if="result || attemptPayload"
				type="secondary"
				:disabled="busy"
				@click="requestClose"
				>{{ $t('application.decline.close') }}</Button
			>
			<Button
				v-if="!attemptPayload"
				type="secondary"
				:disabled="busy"
				data-test="interview-move-without-sending"
				@click="moveWithoutSending"
				>{{
					$t('application.interview-invitation.move-without-sending')
				}}</Button
			>
			<Button
				v-if="
					!attemptPayload ||
						errorKey ===
							'application.interview-invitation.recipients-changed'
				"
				type="primary"
				:disabled="busy || (!attemptPayload && !canSendInvitation)"
				:loading="busy"
				data-test="interview-send-invitation"
				@click="sendInvitation"
				>{{ $t('application.interview-invitation.send') }}</Button
			>
		</template>
	</ModalV2>
</template>
<script>
import ModalV2 from '~/components/Modal/ModalV2'
import Button from '~/components/ui/common/Button'
import TemplateSelector from '~/components/EmailTemplates/TemplateSelector'
import TemplatePreview from '~/components/EmailTemplates/TemplatePreview'
import AddressChips from '~/components/EmailTemplates/AddressChips'
import IconCalendarPlus from '~/components/Icons/IconCalendarPlus'
import IconTimes from '~/components/Icons/IconTimes'
import IconAlertTriangle from '~/components/Icons/IconAlertTriangle'

const validAddresses = addresses =>
	addresses.every(address => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address))
const splitAddresses = value =>
	value
		.split(/[,;]/)
		.map(address => address.trim())
		.filter(Boolean)
function requestId() {
	return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
		(
			Number(c) ^
			(crypto.getRandomValues(new Uint8Array(1))[0] &
				(15 >> (Number(c) / 4)))
		).toString(16)
	)
}
export default {
	name: 'InterviewInvitationModal',
	components: {
		ModalV2,
		Button,
		TemplateSelector,
		TemplatePreview,
		AddressChips,
		IconCalendarPlus,
		IconTimes,
		IconAlertTriangle
	},
	props: {
		value: Boolean,
		applications: { type: Array, default: () => [] },
		submitting: Boolean
	},
	data: () => ({
		context: null,
		selectedId: null,
		contactEmails: [],
		pendingContacts: '',
		manualBcc: [],
		previewLoading: false,
		previewRequest: 0,
		previewTimer: null,
		errorKey: '',
		contactsRequired: false,
		sending: false,
		attemptPayload: null,
		result: null,
		resultHandled: false,
		disposed: false,
		opening: 0,
		swallowEscapeRelease: false
	}),
	computed: {
		applicationIds() {
			return this.applications.map(application => application.id)
		},
		busy() {
			return this.submitting || this.sending
		},
		fieldsLocked() {
			return this.busy || !!this.attemptPayload
		},
		configuredContacts() {
			return this.context?.template.contact_emails || []
		},
		configuredBcc() {
			return this.context?.template.bcc || []
		},
		needsContacts() {
			return (
				!!this.context?.template.requires_contact_emails &&
				!this.configuredContacts.length
			)
		},
		effectiveBcc() {
			return this.configuredBcc.length
				? this.configuredBcc
				: this.manualBcc
		},
		canSendInvitation() {
			const contacts = this.needsContacts
				? [
						...this.contactEmails,
						...splitAddresses(this.pendingContacts)
				  ]
				: this.configuredContacts
			return Boolean(
				this.context &&
					!this.errorKey &&
					!this.previewLoading &&
					(!this.needsContacts || contacts.length) &&
					validAddresses(contacts)
			)
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(open) {
				if (open) this.initialize()
				else this.invalidatePreview()
			}
		},
		applications() {
			if (this.value && !this.fieldsLocked) this.initialize()
		},
		selectedId(id) {
			if (!id || id === this.context?.template.id) return
			this.contactEmails = []
			this.pendingContacts = ''
			this.manualBcc = []
			this.contactsRequired = false
			this.context = null
			this.fetchPreview()
		},
		contactEmails() {
			this.contactsRequired = false
			this.schedulePreview()
		},
		manualBcc() {
			this.schedulePreview()
		}
	},
	mounted() {
		window.addEventListener('keydown', this.captureEscape, true)
		window.addEventListener('keyup', this.releaseEscape, true)
	},
	beforeDestroy() {
		this.disposed = true
		this.invalidatePreview()
		window.removeEventListener('keydown', this.captureEscape, true)
		window.removeEventListener('keyup', this.releaseEscape, true)
		if (!this.sending) this.reconcileDetachedResult()
	},
	methods: {
		captureEscape(event) {
			if (this.value && event.key === 'Escape')
				this.swallowEscapeRelease = true
		},
		releaseEscape(event) {
			// Legacy parent dialogs close on keyup, even when this nested modal is busy.
			if (
				event.key === 'Escape' &&
				(this.value || this.swallowEscapeRelease)
			) {
				event.preventDefault()
				event.stopImmediatePropagation()
				this.swallowEscapeRelease = false
			}
		},
		initialize() {
			if (this.sending) return
			this.invalidatePreview()
			this.context = null
			this.selectedId = null
			this.contactEmails = []
			this.pendingContacts = ''
			this.manualBcc = []
			this.errorKey = ''
			this.contactsRequired = false
			this.attemptPayload = null
			this.result = null
			this.resultHandled = false
			this.opening++
			this.fetchPreview()
		},
		invalidatePreview() {
			clearTimeout(this.previewTimer)
			this.previewRequest++
			this.previewLoading = false
		},
		searchTemplates(search, page) {
			return this.$store.dispatch('application/interviewTemplates', {
				application_ids: this.applicationIds,
				search,
				page
			})
		},
		schedulePreview() {
			clearTimeout(this.previewTimer)
			if (
				!this.value ||
				!this.context ||
				this.fieldsLocked ||
				this.errorKey ===
					'application.interview-invitation.contacts-changed'
			)
				return
			this.previewTimer = setTimeout(() => this.fetchPreview(), 250)
		},
		async fetchPreview() {
			clearTimeout(this.previewTimer)
			if (!this.value || !this.applicationIds.length || this.fieldsLocked)
				return
			const request = ++this.previewRequest
			this.previewLoading = true
			const refreshingContacts =
				this.errorKey ===
				'application.interview-invitation.contacts-changed'
			if (refreshingContacts) {
				this.contactEmails = []
				this.pendingContacts = ''
			}
			try {
				const context = await this.$store.dispatch(
					'application/previewInterviewInvitation',
					{
						application_ids: this.applicationIds,
						...(this.selectedId
							? { template_id: this.selectedId }
							: {}),
						...(this.needsContacts &&
						this.contactEmails.length &&
						!refreshingContacts
							? { contact_emails: this.contactEmails }
							: {}),
						bcc: this.configuredBcc.length ? [] : this.manualBcc
					}
				)
				if (request !== this.previewRequest) return
				this.context = context
				this.selectedId = context.template.id
				this.errorKey = ''
				if (
					this.configuredContacts.length &&
					(this.contactEmails.length || this.pendingContacts)
				) {
					this.contactEmails = []
					this.pendingContacts = ''
				}
				if (this.configuredBcc.length && this.manualBcc.length)
					this.manualBcc = []
			} catch (error) {
				if (request === this.previewRequest)
					this.errorKey =
						error.response?.status === 422
							? 'application.interview-invitation.stale-template'
							: 'application.interview-invitation.request-failed'
			} finally {
				if (request === this.previewRequest) this.previewLoading = false
			}
		},
		sendInvitation() {
			return this.submit(true)
		},
		moveWithoutSending() {
			if (!this.attemptPayload) return this.submit(false)
		},
		async submit(send) {
			if (
				this.busy ||
				this.resultHandled ||
				(this.attemptPayload &&
					this.errorKey !==
						'application.interview-invitation.recipients-changed')
			)
				return
			if (!this.attemptPayload && send) {
				if (this.$refs.contacts && !this.$refs.contacts.add()) return
				if (this.$refs.bcc && !this.$refs.bcc.add()) {
					this.$refs.bccEditor.open = true
					return
				}
				this.contactsRequired =
					this.needsContacts && !this.contactEmails.length
				if (!this.canSendInvitation || this.contactsRequired) return
			}
			this.invalidatePreview()
			this.sending = true
			this.$emit('busy', true)
			if (!this.attemptPayload)
				this.attemptPayload = {
					request_id: requestId(),
					application_ids: [...this.applicationIds],
					send_email: send,
					...(send ? { template_id: this.selectedId } : {}),
					...(send && this.needsContacts
						? { contact_emails: [...this.contactEmails] }
						: {}),
					bcc:
						send && !this.configuredBcc.length
							? [...this.manualBcc]
							: [],
					recipient_ids: send
						? [...(this.context?.recipient_ids || [])]
						: []
				}
			try {
				this.result = await this.$store.dispatch(
					'application/sendInterviewInvitation',
					this.attemptPayload
				)
				this.resultHandled = false
				this.errorKey = ''
			} catch (error) {
				const code = error.response?.data?.code
				if (code === 'recipients_changed') {
					this.attemptPayload = {
						...this.attemptPayload,
						recipient_ids: error.response.data.recipient_ids
					}
					this.context = {
						...this.context,
						recipient_ids: error.response.data.recipient_ids
					}
					this.errorKey =
						'application.interview-invitation.recipients-changed'
				} else if (
					code === 'contacts_changed' ||
					[403, 422].includes(error.response?.status)
				) {
					this.attemptPayload = null
					this.errorKey =
						code === 'contacts_changed'
							? 'application.interview-invitation.contacts-changed'
							: Object.keys(
									error.response?.data?.errors || {}
							  ).some(key =>
									/^(bcc|contact_emails)(\.|$)/.test(key)
							  )
							? 'email-templates.invalid-email'
							: error.response.status === 422
							? 'application.interview-invitation.stale-template'
							: 'application.interview-invitation.request-failed'
				} else this.errorKey = 'application.action.unconfirmed'
			} finally {
				this.sending = false
				this.$emit('busy', false)
				await this.reconcileDetachedResult()
			}
			if (this.result && !this.errorKey) this.complete()
		},
		reconcileDetachedResult() {
			if (!this.disposed || !this.result || this.resultHandled) return
			this.resultHandled = true
			return this.$store.dispatch('application/moveToInterview', {
				interviewResult: this.result,
				refreshList: true
			})
		},
		complete() {
			if (this.disposed || this.resultHandled) return
			this.resultHandled = true
			const session = this.$store.state?.application?.sessionGeneration
			if (
				this.result.sessionGeneration !== undefined &&
				this.result.sessionGeneration !== session
			) {
				this.$emit('input', false)
				this.$emit('close')
				return
			}
			if (!this.result.applications?.length) {
				this.$store.dispatch('application/moveToInterview', {
					interviewResult: this.result
				})
				this.$emit('input', false)
				this.$emit('close')
				return
			}
			this.$emit('submit', { interviewResult: this.result })
		},
		requestClose() {
			if (this.busy) return
			if (this.result?.applications?.length) return this.complete()
			this.$emit('input', false)
			this.$emit('close')
		}
	}
}
</script>
